import { forwardRef, useCallback, useMemo } from 'react';
import {
	FLOATING_POINT_REGEX as CHAKRA_FLOATING_POINT_REGEX,
	NumberInput as ChakraNumberInput,
	NumberInputField,
	NumberInputStepper,
	NumberIncrementStepper,
	NumberDecrementStepper,
} from '@chakra-ui/react';

import { isRTL } from '@eventespresso/i18n';
import { convertThousandsSeparatorToRegExp } from '@eventespresso/utils';
import { defaultCurrency } from '@eventespresso/config';

import type { IntlParameters } from '@eventespresso/types';
import type { NumberInputProps } from './types';

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
	(
		{
			className,
			decrementStepperProps,
			isDisabled,
			id,
			incrementStepperProps,
			inputFieldProps,
			inputStepperProps,
			onChange,
			onChangeValue,
			placeholder,
			showStepper = true,
			value,
			wrapperClass,
			localizeInput,
			...props
		},
		ref
	) => {
		const localizeInputDefault = useMemo<Pick<IntlParameters, 'decimalMark' | 'thousandsSeparator'>>(
			() => ({
				decimalMark: defaultCurrency.decimalMark,
				thousandsSeparator: defaultCurrency.thousandsSeparator,
			}),
			[]
		);

		const ariaValueNow = String(value)?.length ? Number(value) : null;
		const size = inputFieldProps?.size && Number(inputFieldProps?.size);

		const onChangeHandler = useCallback<NumberInputProps['onChange']>(
			(valueAsString, valueAsNumber) => {
				if (!isDisabled) {
					onChange?.(valueAsString, valueAsNumber);
					onChangeValue?.(valueAsNumber);
				}
			},
			[isDisabled, onChange, onChangeValue]
		);

		const stepper = showStepper && (
			// @ts-ignore Expression produces a union type that is too complex to represent.ts(2590)
			<NumberInputStepper {...inputStepperProps}>
				{/* @ts-ignore Expression produces a union type that is too complex to represent.ts(2590) */}
				<NumberIncrementStepper {...incrementStepperProps} />
				{/* @ts-ignore Expression produces a union type that is too complex to represent.ts(2590) */}
				<NumberDecrementStepper {...decrementStepperProps} />
			</NumberInputStepper>
		);

		const overrideFloatingPointRegex = useMemo<RegExp>(() => {
			// default code branch
			if (!localizeInput) {
				return CHAKRA_FLOATING_POINT_REGEX;
			}

			const { thousandsSeparator, decimalMark } = { ...localizeInputDefault, ...localizeInput };

			const thousandsSeparatorOrNull = convertThousandsSeparatorToRegExp(thousandsSeparator);
			let replaceValue = decimalMark;
			// 'literal string' + null results in 'literal stringundefined'
			if (thousandsSeparatorOrNull) {
				replaceValue += thousandsSeparatorOrNull.source;
			}
			// FLOATING_POINT_REGEX has a hardcoded assumption that decimal mark is '.' hence we target it
			return new RegExp(CHAKRA_FLOATING_POINT_REGEX.source.replace('.', replaceValue));
		}, [localizeInput, localizeInputDefault]);

		return (
			// @ts-ignore Expression produces a union type that is too complex to represent.ts(2590)
			<ChakraNumberInput
				// @ts-ignore Expression produces a union type that is too complex to represent.ts(2590)
				{...props}
				overrideFloatingPointRegex={overrideFloatingPointRegex}
				aria-valuenow={ariaValueNow}
				className={wrapperClass}
				isDisabled={isDisabled}
				onChange={onChangeHandler}
				value={value}
				ref={ref}
			>
				{isRTL() && stepper}
				{/* @ts-ignore Expression produces a union type that is too complex to represent.ts(2590) */}
				<NumberInputField
					{...inputFieldProps}
					className={className}
					id={id}
					placeholder={placeholder}
					size={size}
				/>
				{!isRTL() && stepper}
			</ChakraNumberInput>
		);
	}
);

export const FLOATING_POINT_REGEX = CHAKRA_FLOATING_POINT_REGEX;
